import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import axios from 'axios';
import Accounts from './pages/Accounts';
import Subreddits from './pages/Subreddits';
import PostingLists from './pages/PostingLists';
import Scheduler from './pages/Scheduler';
import Stats from './pages/Stats';
import Vault from './pages/Vault';
import Creators from './pages/Creators';
import Users from './pages/Users';
import Spoofer from './pages/Spoofer';
import Converter from './pages/Converter';
import ReplyEngine from './pages/ReplyEngine';
import Competitors from './pages/Competitors';
import Login from './pages/Login';
import { subscribe } from './activity';
import { connectWebSocket, disconnectWebSocket } from './websocket';
import { notify } from './notify';
import './App.css';
import { API } from './config';



axios.interceptors.request.use(function(config) {
  var token = localStorage.getItem('token');
  if (token) config.headers.Authorization = 'Bearer ' + token;
  return config;
});

// Token the request was sent with (AxiosHeaders or a plain object)
function sentToken(config) {
  var h = config && config.headers;
  if (!h) return null;
  var v = typeof h.get === 'function' ? h.get('Authorization') : (h.Authorization || h.authorization);
  return v ? String(v).replace(/^Bearer\s+/i, '') : null;
}

function App() {
  var [user, setUser] = useState(null);
  var [role, setRole] = useState(null);
  var [organizationId, setOrganizationId] = useState(null);
  var [viewingOrgId, setViewingOrgId] = useState(null);
  var [organizations, setOrganizations] = useState([]);
  var [checking, setChecking] = useState(true);
  var [creators, setCreators] = useState([]);
  var [allCreators, setAllCreators] = useState([]); // + archived: the Creators page only
  var [selectedCreator, setSelectedCreator] = useState(null);
  var [activityMessage, setActivityMessage] = useState(null);
  var [initializing, setInitializing] = useState(false); 
  var [navOpen, setNavOpen] = useState(false); // mobile: sidebar collapsed by default

  useEffect(function() {
    return subscribe(setActivityMessage);
  }, []);

  // Expired / revoked login: the server answers 401 → drop the token and show
  // the login screen, once. 403 (org / role denial) is NOT a logout — pages
  // show those errors themselves. The SSE stream reports a dead token via
  // the 'auth-expired' event (it can't see status codes).
  useEffect(function() {
    var expire = function() {
      if (!localStorage.getItem('token')) return; // already handled
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      disconnectWebSocket();
      setUser(null);
      setRole(null);
      setOrganizationId(null);
      setViewingOrgId(null);
      setCreators([]);
      setAllCreators([]);
      setSelectedCreator(null);
      setOrganizations([]);
      setInitializing(false);
      notify('Your login has expired — please sign in again.', 'error', 8000);
    };
    var id = axios.interceptors.response.use(function(res) { return res; }, function(err) {
      var status = err && err.response && err.response.status;
      var url = (err && err.config && err.config.url) || '';
      if (status === 401 && url.indexOf('/api/auth/login') === -1 && url.indexOf('/api/auth/verify') === -1) {
        var current = localStorage.getItem('token');
        // Only if the failing request used the CURRENT token — a late 401
        // from before a fresh login must not log the new session out.
        if (current && sentToken(err.config) === current) expire();
      }
      return Promise.reject(err);
    });
    window.addEventListener('auth-expired', expire);
    return function() {
      axios.interceptors.response.eject(id);
      window.removeEventListener('auth-expired', expire);
    };
  }, []);

  useEffect(function() {
    var token = localStorage.getItem('token');
    if (token) {
      axios.get(API + '/api/auth/verify', {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function(res) {
        setUser(res.data.username);
        setRole(res.data.role);
        setOrganizationId(res.data.organization_id);
        setViewingOrgId(res.data.organization_id);
        connectWebSocket(res.data.organization_id);
        setChecking(false);
      }).catch(function() {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setChecking(false);
      });
    } else {
      setChecking(false);
    }
  }, []);

  useEffect(function() {
    if (user) {
      fetchCreators();
      if (role === 'super_admin') fetchOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role, viewingOrgId]);

  var fetchOrganizations = async function() {
    try {
      var res = await axios.get(API + '/api/organizations');
      setOrganizations(res.data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  var fetchCreators = async function() {
    try {
      var url = API + '/api/creators?include_archived=1';
      if (viewingOrgId) url += '&organization_id=' + viewingOrgId;
      var res = await axios.get(url);
      var active = res.data.filter(function(c) { return !c.archived_at; });
      setAllCreators(res.data);
      setCreators(active);
      var unassigned = active.find(function(c) { return c.name === 'Unassigned'; });
      var saved = localStorage.getItem('selectedCreatorId');
      if (saved) {
        var found = active.find(function(c) { return c.id === saved; });
        if (found) setSelectedCreator(found);
        else if (unassigned) setSelectedCreator(unassigned);
      } else if (unassigned) {
        setSelectedCreator(unassigned);
      }
    } catch (err) {
      console.error('Error fetching creators:', err);
    }
    setInitializing(false);
  };

  var handleLogin = function(username, userRole, orgId) {
    setCreators([]);
    setAllCreators([]);
    setSelectedCreator(null);
    setOrganizations([]);
    setInitializing(true);
    setUser(username);
    setRole(userRole);
    setOrganizationId(orgId);
    setViewingOrgId(orgId);
    connectWebSocket(orgId);
  };
  var [extVersion, setExtVersion] = useState(null);
  var [extDownloading, setExtDownloading] = useState(false);

  useEffect(function() {
    if (!user) return;
    axios.get(API + '/api/extension/version')
      .then(function(r) { setExtVersion(r.data && r.data.version); })
      .catch(function() {});
  }, [user]);

  var downloadExtension = async function() {
    setExtDownloading(true);
    try {
      var res = await axios.get(API + '/api/extension/download', { responseType: 'blob' });
      var url = window.URL.createObjectURL(res.data);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'reddit-rm-tool-extension' + (extVersion ? '-v' + extVersion : '') + '.zip';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      var msg = err.message;
      // Error body comes back as a Blob (responseType blob) — read it to get the
      // real server error instead of a generic "status code 404".
      if (err.response && err.response.data && typeof err.response.data.text === 'function') {
        try { var j = JSON.parse(await err.response.data.text()); if (j.error) msg = j.error; }
        catch (e2) {
          if (err.response.status === 404) msg = '404 — the /api/extension route is not deployed yet (push + wait for the backend redeploy).';
        }
      }
      alert('Download failed: ' + msg);
    }
    setExtDownloading(false);
  };

  var handleLogout = function() {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    disconnectWebSocket();
    setUser(null);
    setRole(null);
    setOrganizationId(null);
    setViewingOrgId(null);
    setCreators([]);
    setAllCreators([]);
    setSelectedCreator(null);
    setOrganizations([]);
  };

  var selectCreator = function(creator) {
    setSelectedCreator(creator);
    if (creator) {
      localStorage.setItem('selectedCreatorId', creator.id);
    } else {
      localStorage.removeItem('selectedCreatorId');
    }
  };

  // Stable identity: pages refetch on creatorAccountIds changes, so it must
  // only change when the selected creator's account list actually changes.
  var accountKey = selectedCreator
    ? (selectedCreator.accounts || []).map(function(a) { return a.id; }).join(',')
    : null;
  var creatorAccountIds = useMemo(function() {
    return accountKey === null ? null : (accountKey ? accountKey.split(',') : []);
  }, [accountKey]);

  if (checking) return <div className="loading-screen">Loading...</div>;
  if (!user) return <Login onLogin={handleLogin} />;
  if (initializing || creators.length === 0) return <div className="loading-screen">Loading...</div>;

  var closeNav = function() { setNavOpen(false); };

  return (
    <Router>
      <div className="app">
        <nav className={'sidebar' + (navOpen ? ' open' : '')} onClick={function(e) { if (e.target.tagName === 'A') closeNav(); }}>
          <div className="sidebar-top">
            <div className="logo">Reddit RM Tool</div>
            <button
              type="button"
              className="nav-toggle"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              onClick={function() { setNavOpen(!navOpen); }}
            >{navOpen ? '✕' : '☰'}</button>
          </div>
          <div className="current-creator">
            <select
              value={selectedCreator?.id || ''}
              onChange={function(e) {
                var creator = creators.find(function(c) { return c.id === e.target.value; });
                selectCreator(creator);
              }}
              style={{
                background: '#1a1a1a', color: '#ff4500', border: 'none',
                fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                width: '100%', padding: '4px 0'
              }}
            >
              {creators.map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
          </div>
          <NavLink to="/creators">Creators</NavLink>
          <NavLink to="/" end>Accounts</NavLink>
          <NavLink to="/subreddits">Subreddits</NavLink>
          <NavLink to="/posting-lists">Posting Lists</NavLink>
          <NavLink to="/scheduler" style={function(props) {
            return {
              background: props.isActive ? '#ff4500' : 'transparent',
              color: props.isActive ? '#ffffff' : '#ff4500',
              border: '2px solid #ff4500',
              padding: '10px 16px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '15px',
              textAlign: 'center',
              textDecoration: 'none',
              display: 'block',
              marginTop: '4px',
              marginBottom: '4px'
            };
          }}>Post & Schedule</NavLink>
          <NavLink to="/vault">Vault</NavLink>
          {role !== 'poster_va' && <NavLink to="/stats">Statistics</NavLink>}
          {role !== 'poster_va' && <NavLink to="/reply-engine">Reply Engine</NavLink>}
          {role !== 'poster_va' && <NavLink to="/competitors">Competitors</NavLink>}
          {role === 'super_admin' && <NavLink to="/spoofer">Spoofer</NavLink>}
          {role === 'super_admin' && <NavLink to="/converter">Converter</NavLink>}
          {(role === 'super_admin' || role === 'admin') && <NavLink to="/users">Users</NavLink>}
          <div className="sidebar-bottom">
            {role === 'super_admin' && organizations.length > 0 && (
              <select
                value={viewingOrgId || organizationId || ''}
                onChange={function(e) { setViewingOrgId(e.target.value || null); setSelectedCreator(null); }}
                style={{ width: '100%', fontSize: '11px', padding: '4px', marginBottom: '8px', background: '#1a1a1a', color: '#f59e0b', border: '1px solid #333', borderRadius: '6px' }}
              >
                {organizations.map(function(org) {
                  return <option key={org.id} value={org.id}>{org.name}</option>;
                })}
              </select>
            )}
            <button
              onClick={downloadExtension}
              disabled={extDownloading}
              title="Download the latest Chrome extension (load unpacked in Dolphin)"
              style={{ width: '100%', fontSize: '11px', padding: '6px', marginBottom: '8px', background: '#1a1a1a', color: '#f59e0b', border: '1px solid #92400e', borderRadius: '6px', cursor: extDownloading ? 'wait' : 'pointer' }}
            >
              {extDownloading ? 'Preparing…' : ('⬇ Download extension' + (extVersion ? ' (v' + extVersion + ')' : ''))}
            </button>
            <span className="logged-in-as">Logged in as {user}</span>
            <button className="btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </nav>
        <main className="content">
          <Routes>
            <Route path="/creators" element={
              <Creators
                creators={allCreators}
                selectedCreator={selectedCreator}
                onSelectCreator={selectCreator}
                onUpdate={fetchCreators}
                role={role}
                viewingOrgId={viewingOrgId}
              />
            } />
            <Route path="/" element={<Accounts creatorAccountIds={creatorAccountIds} selectedCreator={selectedCreator} onAccountsChanged={fetchCreators} role={role} />} />
            <Route path="/subreddits" element={<Subreddits role={role} viewingOrgId={viewingOrgId} />} />
            <Route path="/posting-lists" element={<PostingLists creatorAccountIds={creatorAccountIds} role={role} />} />
            <Route path="/scheduler" element={
              <Scheduler
                selectedCreator={selectedCreator}
                creatorAccountIds={creatorAccountIds}
                role={role}
              />
            } />
            <Route path="/vault" element={<Vault selectedCreator={selectedCreator} role={role} organizationId={organizationId} />} />
            <Route path="/stats" element={
              role !== 'poster_va'
                ? <Stats creatorAccountIds={creatorAccountIds} selectedCreator={selectedCreator} role={role} />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
            <Route path="/reply-engine" element={
              role !== 'poster_va'
                ? <ReplyEngine role={role} creators={creators} />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
            <Route path="/competitors" element={
              role !== 'poster_va'
                ? <Competitors viewingOrgId={viewingOrgId} />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
            <Route path="/users" element={
              (role === 'super_admin' || role === 'admin')
                ? <Users role={role} />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
            <Route path="/spoofer" element={
              role === 'super_admin'
                ? <Spoofer />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
            <Route path="/converter" element={
              role === 'super_admin'
                ? <Converter />
                : <div className="page"><div className="card"><p className="empty">Access denied.</p></div></div>
            } />
          </Routes>
        </main>
      </div>
      {activityMessage && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px',
          background: '#1a1a1a', border: '2px solid #ff4500',
          borderRadius: '10px', padding: '12px 18px',
          zIndex: 9999, boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', gap: '10px',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            width: '18px', height: '18px', border: '2px solid #ff4500',
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <div>
            <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '500' }}>
              {activityMessage}
            </span>
            <div style={{ fontSize: '10px', color: '#888', marginTop: '3px' }}>
              Please stay on this page
            </div>
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;